import React, { useEffect, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { useConfig } from "@/providers/config"
import { debugVersionCheck as debug } from "@/utils/debug"

interface VersionResponse {
  version: string
}

const POLL_INTERVAL = 5 * 60 * 1000 // 5 minutes
const NOTIFICATION_RESHOW_INTERVAL = 10 * 60 * 1000 // 10 minutes

export function useVersionCheck() {
  const { config, isLoading } = useConfig()
  const [isTabVisible, setIsTabVisible] = useState(true)
  const [lastDismissed, setLastDismissed] = useState<number | null>(null)

  // Capture client version once when config first loads
  // Using useMemo to derive the version only when config becomes available
  const clientVersion = React.useMemo(() => {
    if (!isLoading && config) {
      const version = config.version || "dev"
      debug("Config loaded, capturing client version: %s", version)
      debug("Config: %o", config)
      return version
    }
    return null
  }, [isLoading, config])

  // Log initial state
  useEffect(() => {
    debug("Hook mounted")
    debug("Config loading: %s", isLoading)
    debug("Environment: %s", config?.environment)
    debug("Client version: %s", clientVersion)
    debug("Tab visible: %s", isTabVisible)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only log on initial mount
  }, [])

  // Track tab visibility to pause polling when inactive
  useEffect(() => {
    const handleVisibilityChange = () => {
      const visible = !document.hidden
      debug("Tab visibility changed: %s", visible)
      setIsTabVisible(visible)
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [])

  // Check for development override in localStorage
  // Enable in browser console:
  // - Force version check: localStorage.setItem('FORCE_VERSION_CHECK', 'true')
  // - Enable debug logs: localStorage.debug = 'version:*'
  const forceVersionCheck =
    typeof window !== "undefined" &&
    localStorage.getItem("FORCE_VERSION_CHECK") === "true"

  // Only enable polling in production (or when forced in development) and when tab is visible
  // Also require clientVersion to be loaded
  const isEnabled =
    (config?.environment === "production" || forceVersionCheck) &&
    isTabVisible &&
    clientVersion !== null &&
    !isLoading

  // Log enabled state changes
  useEffect(() => {
    debug("Query enabled state: %o", {
      isEnabled,
      environment: config?.environment,
      forceVersionCheck,
      isTabVisible,
      clientVersion,
      isLoading,
      isProduction: config?.environment === "production",
    })
  }, [
    isEnabled,
    config?.environment,
    forceVersionCheck,
    isTabVisible,
    clientVersion,
    isLoading,
  ])

  const { data } = useQuery<VersionResponse>({
    queryKey: ["version"],
    queryFn: async () => {
      debug("Fetching version from server...")
      const response = await fetch("/api/v1/version")
      if (!response.ok) {
        debug("Failed to fetch version: %d", response.status)
        throw new Error("Failed to fetch version")
      }
      const json = await response.json()
      debug("Received server response: %o", json)
      return json
    },
    enabled: isEnabled,
    refetchInterval: POLL_INTERVAL,
    // Don't refetch on window focus to avoid too many requests
    refetchOnWindowFocus: false,
    // Keep previous data while refetching
    placeholderData: (previousData) => previousData,
  })

  // Log data changes
  useEffect(() => {
    if (data?.version) {
      debug("Query data updated: %o", {
        serverVersion: data.version,
        clientVersion,
        dataTimestamp: new Date().toISOString(),
      })
    }
  }, [data, clientVersion])

  // Derive notification state from current conditions.
  // `lastDismissed` is cleared via timer after the cooldown, so its presence
  // alone indicates the notification is still suppressed.
  const showNotification = Boolean(
    data?.version &&
    isEnabled &&
    clientVersion &&
    data.version !== clientVersion &&
    lastDismissed === null,
  )

  const handleDismiss = () => {
    debug("User dismissed notification")
    setLastDismissed(Date.now())
    setTimeout(() => setLastDismissed(null), NOTIFICATION_RESHOW_INTERVAL)
  }

  const handleRefresh = () => {
    debug("User clicked refresh, reloading page...")
    window.location.reload()
  }

  // Log notification state changes
  useEffect(() => {
    debug("Notification state changed: %s", showNotification)
  }, [showNotification])

  // Log summary of current state periodically
  useEffect(() => {
    const interval = setInterval(() => {
      debug("=== Current State Summary === %o", {
        clientVersion: clientVersion || "not loaded yet",
        serverVersion: data?.version || "not fetched yet",
        isEnabled,
        showNotification,
        environment: config?.environment || "not loaded yet",
        forceVersionCheck,
        isTabVisible,
        isLoading,
        lastDismissed: lastDismissed
          ? new Date(lastDismissed).toISOString()
          : "never",
        nextPollIn: isEnabled
          ? `~${Math.round(POLL_INTERVAL / 1000 / 60)} minutes`
          : "disabled",
      })
    }, 30000) // Log every 30 seconds

    return () => clearInterval(interval)
  }, [
    clientVersion,
    data,
    isEnabled,
    showNotification,
    config?.environment,
    forceVersionCheck,
    isTabVisible,
    isLoading,
    lastDismissed,
  ])

  return {
    showNotification,
    handleDismiss,
    handleRefresh,
    serverVersion: data?.version,
    clientVersion,
  }
}
